import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import {
  Search,
  X,
  BookOpen,
  FileText,
  Target,
  Compass,
  ListChecks,
  Lightbulb,
  GitCompare,
  AlertCircle,
} from "lucide-react";
import { CHAPTERS } from "@/data/chapters";
import {
  buildIndex,
  searchIndex,
  highlight,
  type SearchHit,
  type SearchHitKind,
} from "@/lib/search";

const KIND_META: Record<SearchHitKind, { label: string; icon: any }> = {
  chapter: { label: "Chapters", icon: BookOpen },
  glossary: { label: "Glossary", icon: FileText },
  concept: { label: "Concepts", icon: Compass },
  objective: { label: "Objectives", icon: Target },
  procedure: { label: "Procedures", icon: ListChecks },
  example: { label: "Examples", icon: Lightbulb },
  comparison: { label: "Comparisons", icon: GitCompare },
  misconception: { label: "Misconceptions", icon: AlertCircle },
};

const GROUP_ORDER: SearchHitKind[] = [
  "chapter",
  "glossary",
  "concept",
  "objective",
  "procedure",
  "comparison",
  "example",
  "misconception",
];

export function CommandSearch({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [, setLocation] = useLocation();

  const index = useMemo(() => buildIndex(CHAPTERS), []);
  const results = useMemo(() => searchIndex(index, query, 60), [index, query]);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setActiveIdx(0);
    const t = window.setTimeout(() => inputRef.current?.focus(), 20);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.clearTimeout(t);
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => setActiveIdx(0), [query]);

  const choose = (hit: SearchHit) => {
    if (hit.anchor) sessionStorage.setItem("__jumpTo", hit.anchor);
    setLocation(`/chapter/${hit.chapterId}`);
    onClose();
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx(i => Math.min(i + 1, results.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx(i => Math.max(i - 1, 0));
      } else if (e.key === "Enter") {
        const hit = results[activeIdx];
        if (hit) {
          e.preventDefault();
          choose(hit);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, results, activeIdx]);

  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(
      `[data-result-idx="${activeIdx}"]`,
    );
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIdx, open]);

  if (!open) return null;

  const grouped: Partial<Record<SearchHitKind, SearchHit[]>> = {};
  for (const r of results) {
    (grouped[r.kind] ??= []).push(r);
  }
  const orderedHits: SearchHit[] = [];
  for (const k of GROUP_ORDER) {
    const list = grouped[k];
    if (!list) continue;
    orderedHits.push(...list);
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[60] flex items-start sm:items-center justify-center sm:p-6"
      data-testid="command-search"
    >
      <div
        className="absolute inset-0 bg-background/85 sm:bg-background/60"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="relative w-full sm:max-w-2xl bg-card sm:rounded-xl border-b sm:border border-border shadow-2xl flex flex-col max-h-dvh sm:max-h-[78vh]"
        onMouseDown={e => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-3 sm:px-4 py-3 border-b border-border">
          <Search className="w-4 h-4 text-muted-foreground shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search chapters, terms, objectives, examples..."
            className="flex-1 bg-transparent outline-none text-[15px] sm:text-sm placeholder:text-muted-foreground"
            data-testid="command-search-input"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            enterKeyHint="search"
          />
          {query && (
            <button
              type="button"
              onClick={() => {
                setQuery("");
                inputRef.current?.focus();
              }}
              className="p-1.5 rounded hover-elevate text-muted-foreground"
              aria-label="Clear search"
              data-testid="command-search-clear"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="hidden sm:inline-flex items-center gap-1 text-[10.5px] font-medium uppercase tracking-wider px-2 py-1 rounded border border-border text-muted-foreground hover-elevate"
            aria-label="Close"
          >
            Esc
          </button>
        </div>

        <div ref={listRef} className="flex-1 overflow-y-auto overscroll-contain">
          {!query && (
            <div className="px-4 py-10 text-center text-muted-foreground">
              <div className="text-[13px] mb-3">Start typing to search 31 chapters</div>
              <div className="text-[11px] inline-flex flex-wrap items-center gap-1.5 justify-center">
                <kbd className="px-1.5 py-0.5 rounded border border-border bg-muted/40 font-mono text-[10px]">/</kbd>
                <span>or</span>
                <kbd className="px-1.5 py-0.5 rounded border border-border bg-muted/40 font-mono text-[10px]">Cmd</kbd>
                <span>+</span>
                <kbd className="px-1.5 py-0.5 rounded border border-border bg-muted/40 font-mono text-[10px]">K</kbd>
                <span>to open</span>
                <span className="text-muted-foreground/50">·</span>
                <kbd className="px-1.5 py-0.5 rounded border border-border bg-muted/40 font-mono text-[10px]">Esc</kbd>
                <span>to close</span>
              </div>
            </div>
          )}

          {query && results.length === 0 && (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground">
              No matches for "{query}".
            </div>
          )}

          {query && results.length > 0 && (
            <div className="py-2">
              {GROUP_ORDER.map(k => {
                const items = grouped[k];
                if (!items || items.length === 0) return null;
                const Icon = KIND_META[k].icon;
                return (
                  <div key={k} className="px-2 pb-2">
                    <div className="px-3 py-1.5 text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground flex items-center gap-1.5">
                      <Icon className="w-3 h-3" />
                      {KIND_META[k].label}
                      <span className="tabular-nums text-muted-foreground/70">({items.length})</span>
                    </div>
                    <ul>
                      {items.map(hit => {
                        const myIdx = orderedHits.indexOf(hit);
                        const active = myIdx === activeIdx;
                        return (
                          <li key={`${hit.chapterId}-${hit.kind}-${myIdx}`}>
                            <button
                              type="button"
                              data-result-idx={myIdx}
                              onMouseEnter={() => setActiveIdx(myIdx)}
                              onClick={() => choose(hit)}
                              className={`w-full text-left px-3 py-2.5 rounded-md flex items-start gap-3 min-h-[48px] ${active ? "bg-muted text-foreground" : "text-foreground/90 hover-elevate"}`}
                              data-testid={`command-search-result-${myIdx}`}
                            >
                              <span className="text-[10px] font-mono tabular-nums text-primary mt-0.5 shrink-0">
                                CH·{hit.chapterId}
                              </span>
                              <span className="flex-1 min-w-0">
                                <span className="block text-[13.5px] sm:text-[13px] font-medium leading-snug truncate">
                                  <Highlighted text={hit.label} query={query} />
                                </span>
                                {hit.snippet && (
                                  <span className="block text-[12px] text-muted-foreground leading-snug line-clamp-1 mt-0.5">
                                    <Highlighted text={hit.snippet} query={query} />
                                  </span>
                                )}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {query && results.length > 0 && (
          <div className="hidden sm:flex items-center justify-between gap-3 px-3 py-2 border-t border-border text-[11px] text-muted-foreground">
            <div className="flex items-center gap-2">
              <kbd className="px-1.5 py-0.5 rounded border border-border bg-muted/40 font-mono text-[10px]">↑</kbd>
              <kbd className="px-1.5 py-0.5 rounded border border-border bg-muted/40 font-mono text-[10px]">↓</kbd>
              <span>navigate</span>
              <kbd className="ml-2 px-1.5 py-0.5 rounded border border-border bg-muted/40 font-mono text-[10px]">Enter</kbd>
              <span>open</span>
            </div>
            <span className="tabular-nums">
              {results.length} result{results.length === 1 ? "" : "s"}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function Highlighted({ text, query }: { text: string; query: string }) {
  const parts = highlight(text, query);
  return (
    <>
      {parts.map((p, i) =>
        p.match ? (
          <mark key={i} className="bg-primary/20 text-primary rounded-sm px-0.5 py-0">
            {p.text}
          </mark>
        ) : (
          <span key={i}>{p.text}</span>
        ),
      )}
    </>
  );
}

export function useGlobalSearchHotkey(open: () => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const inEditable = !!target?.closest(
        "input, textarea, [contenteditable='true']",
      );
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        open();
        return;
      }
      if (e.key === "/" && !inEditable) {
        e.preventDefault();
        open();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
}

export function SearchTriggerButton({
  onClick,
  variant = "compact",
}: {
  onClick: () => void;
  variant?: "compact" | "wide";
}) {
  if (variant === "wide") {
    return (
      <button
        type="button"
        onClick={onClick}
        className="w-full max-w-md flex items-center gap-2.5 px-3 py-2.5 text-left rounded-md border border-input bg-background hover-elevate text-[14px] sm:text-[13.5px] text-muted-foreground min-h-[44px]"
        data-testid="search-trigger-wide"
        aria-label="Open search"
      >
        <Search className="w-4 h-4 shrink-0" />
        <span className="flex-1 truncate">Search chapters, terms, examples...</span>
        <span className="hidden sm:inline-flex items-center gap-1 text-[10px]">
          <kbd className="px-1.5 py-0.5 rounded border border-border bg-muted/40 font-mono text-[10px]">
            /
          </kbd>
        </span>
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className="p-2.5 rounded-md border border-card-border hover-elevate min-w-[40px] min-h-[40px] flex items-center justify-center"
      data-testid="search-trigger-compact"
      aria-label="Open search"
    >
      <Search className="w-4 h-4" />
    </button>
  );
}
